import {
  combineCounts,
  formatFormula,
  WATER,
  type ChoiceOption,
  type ChoiceQuestion,
  type ElementCounts,
} from './polymersBiomolecules';
import {
  AMINO_ACIDS,
  buildPeptide,
  buildTriglyceride,
  FATTY_ACIDS,
  FATTY_ACIDS_PER_TRIGLYCERIDE,
  FRUCTOSE,
  GLUCOSE,
  GLYCEROL,
} from './polymersBiomoleculesBio';

// Digestion as hydrolysis and food energy for the `food-enzymes` mode. Data meta and sources are
// declared in `FOOD_DATA_META` (polymersBiomoleculesFood.ts).

// ---------------------------------------------------------------------------------------------
// Digestion as hydrolysis
// ---------------------------------------------------------------------------------------------

export type HydrolysedBond = 'glycosidic' | 'peptide' | 'ester';

export const HYDROLYSED_BOND_OPTIONS: { id: HydrolysedBond; label: string }[] = [
  { id: 'glycosidic', label: 'Glycosidic C–O–C' },
  { id: 'peptide', label: 'Peptide –CO–NH–' },
  { id: 'ester', label: 'Ester –COO–' },
];

export const WATER_COUNT_OPTIONS = [1, 2, 3, 4] as const;

export interface HydrolysisProduct {
  name: string;
  coefficient: number;
  counts: ElementCounts;
}

export interface HydrolysisCase {
  id: string;
  label: string;
  foodGroup: 'carbohydrate' | 'protein' | 'fat';
  reactantName: string;
  reactantCounts: ElementCounts;
  enzyme: string;
  bond: HydrolysedBond;
  /** Water used = number of links broken. */
  water: number;
  products: HydrolysisProduct[];
  note: string;
}

const GALACTOSE: ElementCounts = { C: 6, H: 12, O: 6 };
const DISACCHARIDE: ElementCounts = { C: 12, H: 22, O: 11 };
/** A four-glucose fragment of amylose: 4 × C₆H₁₂O₆ − 3 H₂O. */
const STARCH_FRAGMENT: ElementCounts = { C: 24, H: 42, O: 21 };

function acidCounts(id: string): { name: string; counts: ElementCounts } {
  const acid = AMINO_ACIDS.find((item) => item.id === id)!;
  return { name: acid.name, counts: acid.formula };
}

function peptideCase(id: string, ids: string[], enzyme: string, note: string): HydrolysisCase {
  const result = buildPeptide(ids);
  if (!result.ok) throw new Error(`Reviewed peptide ${id} failed to build: ${result.message}`);
  const { peptide } = result;
  const products = new Map<string, HydrolysisProduct>();
  for (const acidId of ids) {
    const { name, counts } = acidCounts(acidId);
    const existing = products.get(name);
    if (existing) existing.coefficient += 1;
    else products.set(name, { name, coefficient: 1, counts });
  }
  return {
    id,
    label: `${peptide.kind} ${peptide.name}`,
    foodGroup: 'protein',
    reactantName: `${peptide.kind} ${peptide.name}`,
    reactantCounts: peptide.counts,
    enzyme,
    bond: 'peptide',
    water: peptide.peptideBonds,
    products: [...products.values()],
    note,
  };
}

function fatCase(id: string, fattyAcidId: string, note: string): HydrolysisCase {
  const triglyceride = buildTriglyceride(fattyAcidId);
  const fattyAcid = FATTY_ACIDS.find((item) => item.id === fattyAcidId);
  if (!triglyceride || !fattyAcid) throw new Error(`Reviewed fat ${id} is missing.`);
  return {
    id,
    label: `${triglyceride.name} (${triglyceride.fattyAcid.triglycerideState})`,
    foodGroup: 'fat',
    reactantName: triglyceride.name,
    reactantCounts: triglyceride.counts,
    enzyme: 'lipase',
    bond: 'ester',
    water: FATTY_ACIDS_PER_TRIGLYCERIDE,
    products: [
      { name: 'glycerol', coefficient: 1, counts: GLYCEROL },
      {
        name: fattyAcid.name.split(' (')[0],
        coefficient: FATTY_ACIDS_PER_TRIGLYCERIDE,
        counts: fattyAcid.counts,
      },
    ],
    note,
  };
}

export const HYDROLYSIS_CASES: HydrolysisCase[] = [
  {
    id: 'maltose',
    label: 'maltose (from starch)',
    foodGroup: 'carbohydrate',
    reactantName: 'maltose',
    reactantCounts: DISACCHARIDE,
    enzyme: 'maltase',
    bond: 'glycosidic',
    water: 1,
    products: [{ name: 'glucose', coefficient: 2, counts: GLUCOSE }],
    note: 'Maltase on the small-intestine lining finishes starch digestion, giving glucose that can be absorbed.',
  },
  {
    id: 'sucrose',
    label: 'sucrose (table sugar)',
    foodGroup: 'carbohydrate',
    reactantName: 'sucrose',
    reactantCounts: DISACCHARIDE,
    enzyme: 'sucrase',
    bond: 'glycosidic',
    water: 1,
    products: [
      { name: 'glucose', coefficient: 1, counts: GLUCOSE },
      { name: 'fructose', coefficient: 1, counts: FRUCTOSE },
    ],
    note: 'Glucose and fructose are isomers: same formula C₆H₁₂O₆, different structures.',
  },
  {
    id: 'lactose',
    label: 'lactose (milk sugar)',
    foodGroup: 'carbohydrate',
    reactantName: 'lactose',
    reactantCounts: DISACCHARIDE,
    enzyme: 'lactase',
    bond: 'glycosidic',
    water: 1,
    products: [
      { name: 'glucose', coefficient: 1, counts: GLUCOSE },
      { name: 'galactose', coefficient: 1, counts: GALACTOSE },
    ],
    note: 'People with low lactase activity cannot break this link well, so lactose reaches the large intestine undigested.',
  },
  {
    id: 'starch-fragment',
    label: 'starch fragment (4 glucose units)',
    foodGroup: 'carbohydrate',
    reactantName: 'four-unit starch fragment',
    reactantCounts: STARCH_FRAGMENT,
    enzyme: 'amylase',
    bond: 'glycosidic',
    water: 1,
    products: [{ name: 'maltose', coefficient: 2, counts: DISACCHARIDE }],
    note: 'Simplified: a real starch chain has hundreds to thousands of glucose units, and amylase breaks it into maltose and short chains, not one tidy split.',
  },
  peptideCase(
    'gly-ala',
    ['gly', 'ala'],
    'a peptidase',
    'Peptidases on the small-intestine lining finish protein digestion so single amino acids can be absorbed.',
  ),
  peptideCase(
    'gly-ala-ser',
    ['gly', 'ala', 'ser'],
    'proteases and peptidases',
    'Proteins are hydrolysed in stages: pepsin and trypsin cut long chains, then peptidases break the last links.',
  ),
  fatCase(
    'tristearin',
    'stearic',
    'Complete hydrolysis is shown. In the gut, pancreatic lipase mostly removes the two outer fatty acids and leaves a monoglyceride.',
  ),
  fatCase(
    'triolein',
    'oleic',
    'The C=C in each oleic acid chain is not touched: hydrolysis only breaks the ester links.',
  ),
];

export interface HydrolysisAnswer {
  bond: HydrolysedBond;
  water: number;
}

export type HydrolysisResult =
  | {
      status: 'checked';
      bondCorrect: boolean;
      waterCorrect: boolean;
      bondFeedback: string;
      waterFeedback: string;
    }
  | { status: 'unsupported'; message: string };

const BOND_TEXT: Record<HydrolysedBond, string> = {
  glycosidic: 'glycosidic links between sugar units',
  peptide: 'peptide links between amino acids',
  ester: 'ester links between glycerol and fatty acids',
};

const FOOD_GROUP_BOND: Record<HydrolysisCase['foodGroup'], string> = {
  carbohydrate: 'Carbohydrates are held together by glycosidic links',
  protein: 'Proteins are held together by peptide links',
  fat: 'Fats and oils are held together by ester links',
};

export function evaluateHydrolysis(caseId: string, answer: HydrolysisAnswer): HydrolysisResult {
  const item = HYDROLYSIS_CASES.find((entry) => entry.id === caseId);
  const bondKnown = HYDROLYSED_BOND_OPTIONS.some((option) => option.id === answer.bond);
  const waterKnown = (WATER_COUNT_OPTIONS as readonly number[]).includes(answer.water);
  if (!item || !bondKnown || !waterKnown) {
    return {
      status: 'unsupported',
      message: 'That food molecule, link or water count is not in the reviewed set.',
    };
  }
  const bondCorrect = answer.bond === item.bond;
  const waterCorrect = answer.water === item.water;
  const bondFeedback = bondCorrect
    ? `Correct: ${item.enzyme} breaks the ${BOND_TEXT[item.bond]} in ${item.reactantName}.`
    : `${item.reactantName} has no ${BOND_TEXT[answer.bond]}. ${FOOD_GROUP_BOND[item.foodGroup]}, so ${item.enzyme} breaks ${BOND_TEXT[item.bond]}.`;
  const links = item.water === 1 ? '1 link' : `${item.water} links`;
  let waterFeedback: string;
  if (waterCorrect) {
    waterFeedback = `Correct: ${item.reactantName} has ${links} to break, and each hydrolysis adds one H₂O (H to one side, OH to the other).`;
  } else if (answer.water < item.water) {
    waterFeedback = `${answer.water} H₂O would break only ${answer.water} of the ${links}. Complete hydrolysis needs one water molecule for every link: ${item.water} H₂O.`;
  } else {
    waterFeedback = `${item.reactantName} has only ${links} to break, so only ${item.water} H₂O can react. Hydrolysis uses exactly one water molecule per link; the extra water is not part of the equation.`;
  }
  return { status: 'checked', bondCorrect, waterCorrect, bondFeedback, waterFeedback };
}

export function hydrolysisProductCounts(item: HydrolysisCase): ElementCounts {
  return combineCounts(
    ...item.products.map((product) => ({ counts: product.counts, times: product.coefficient })),
  );
}

export function hydrolysisReactantCounts(item: HydrolysisCase): ElementCounts {
  return combineCounts({ counts: item.reactantCounts }, { counts: WATER, times: item.water });
}

function coefficientText(value: number): string {
  return value === 1 ? '' : String(value);
}

export function hydrolysisEquation(item: HydrolysisCase): string {
  const left = `${formatFormula(item.reactantCounts)} + ${coefficientText(item.water)}H₂O`;
  const right = item.products
    .map((product) => `${coefficientText(product.coefficient)}${formatFormula(product.counts)}`)
    .join(' + ');
  return `${left} → ${right}`;
}

export function hydrolysisWordEquation(item: HydrolysisCase): string {
  const right = item.products
    .map((product) => `${product.coefficient === 1 ? '' : `${product.coefficient} `}${product.name}`)
    .join(' + ');
  const water = item.water === 1 ? 'water' : `${item.water} water`;
  return `${item.reactantName} + ${water} → ${right}`;
}

// ---------------------------------------------------------------------------------------------
// Food energy
// ---------------------------------------------------------------------------------------------

export type Nutrient = 'protein' | 'carbohydrate' | 'fat';
export const NUTRIENTS: readonly Nutrient[] = ['protein', 'carbohydrate', 'fat'];

/** kJ per gram. Senior chemistry values; protein and fat match FSANZ label factors. */
export const ENERGY_FACTORS_KJ_PER_G: Record<Nutrient, number> = {
  protein: 17,
  carbohydrate: 16,
  fat: 37,
};

/** kcal per gram (Atwater). Used only to build the "kcal mistaken for kJ" distractor. */
const KCAL_FACTORS_PER_G: Record<Nutrient, number> = { protein: 4, carbohydrate: 4, fat: 9 };

export interface FoodSample {
  id: string;
  name: string;
  serve: string;
  grams: Record<Nutrient, number>;
}

/** Rounded illustrative compositions for one serve; not a specific product label. */
export const FOOD_SAMPLES: FoodSample[] = [
  { id: 'bread', name: 'Wholemeal bread', serve: '1 slice, 40 g', grams: { protein: 4, carbohydrate: 16, fat: 1 } },
  { id: 'milk', name: 'Full-cream milk', serve: '1 glass, 250 mL', grams: { protein: 8.5, carbohydrate: 12, fat: 9.5 } },
  { id: 'egg', name: 'Boiled egg', serve: '1 egg, 50 g', grams: { protein: 6.5, carbohydrate: 0.3, fat: 5 } },
  { id: 'banana', name: 'Banana', serve: '1 medium, 120 g', grams: { protein: 1.3, carbohydrate: 24, fat: 0.3 } },
  { id: 'almonds', name: 'Almonds', serve: '1 small handful, 30 g', grams: { protein: 6, carbohydrate: 1.5, fat: 15 } },
  { id: 'rice', name: 'Cooked white rice', serve: '1 cup, 150 g', grams: { protein: 4, carbohydrate: 42, fat: 0.5 } },
  { id: 'tuna', name: 'Tuna in spring water', serve: '1 small tin, 95 g', grams: { protein: 24, carbohydrate: 0, fat: 1 } },
  { id: 'butter', name: 'Butter', serve: '1 teaspoon, 5 g', grams: { protein: 0, carbohydrate: 0, fat: 4 } },
];

export interface EnergyContribution {
  nutrient: Nutrient;
  grams: number;
  factor: number;
  kilojoules: number;
}

export interface FoodEnergy {
  contributions: EnergyContribution[];
  totalKilojoules: number;
}

export function foodEnergy(grams: Record<Nutrient, number>): FoodEnergy | null {
  if (NUTRIENTS.some((nutrient) => !Number.isFinite(grams[nutrient]) || grams[nutrient] < 0)) {
    return null;
  }
  const contributions = NUTRIENTS.map((nutrient) => ({
    nutrient,
    grams: grams[nutrient],
    factor: ENERGY_FACTORS_KJ_PER_G[nutrient],
    kilojoules: grams[nutrient] * ENERGY_FACTORS_KJ_PER_G[nutrient],
  }));
  return {
    contributions,
    totalKilojoules: contributions.reduce((sum, item) => sum + item.kilojoules, 0),
  };
}

export function roundKilojoules(value: number): number {
  return Math.round(value);
}

/** Distractors closer than this fraction to another option are dropped as too similar. */
const MIN_OPTION_GAP = 0.05;
const ENERGY_OPTION_COUNT = 4;

const F = ENERGY_FACTORS_KJ_PER_G;

interface EnergyDistractor {
  id: string;
  value: (grams: Record<Nutrient, number>) => number;
  feedback: string;
}

const ENERGY_DISTRACTORS: EnergyDistractor[] = [
  {
    id: 'kcal',
    value: (g) =>
      NUTRIENTS.reduce((sum, nutrient) => sum + g[nutrient] * KCAL_FACTORS_PER_G[nutrient], 0),
    feedback:
      'That used 4, 4 and 9, which are kilocalories per gram, not kilojoules. Australian labels report energy in kJ. In senior chemistry use 17 (protein), 16 (carbohydrate) and 37 (fat) kJ g⁻¹.',
  },
  {
    id: 'swap-carb-fat',
    value: (g) => g.protein * F.protein + g.carbohydrate * F.fat + g.fat * F.carbohydrate,
    feedback:
      'The carbohydrate and fat factors are swapped. Fat releases the most energy per gram (37 kJ g⁻¹) because its chains hold many C–H bonds and little oxygen; carbohydrate releases 16 kJ g⁻¹.',
  },
  {
    id: 'omit-fat',
    value: (g) => g.protein * F.protein + g.carbohydrate * F.carbohydrate,
    feedback:
      'The fat has been left out. Every energy-giving nutrient counts: add fat × 37 kJ g⁻¹.',
  },
  {
    id: 'all-17',
    value: (g) => (g.protein + g.carbohydrate + g.fat) * F.protein,
    feedback:
      'Each nutrient needs its own factor. 17 kJ g⁻¹ is right only for protein; carbohydrate is 16 and fat is 37.',
  },
  {
    id: 'protein-37',
    value: (g) => g.protein * F.fat + g.carbohydrate * F.carbohydrate + g.fat * F.protein,
    feedback:
      'The protein and fat factors are swapped. Protein releases 17 kJ g⁻¹ and fat releases 37 kJ g⁻¹.',
  },
  {
    id: 'omit-carbohydrate',
    value: (g) => g.protein * F.protein + g.fat * F.fat,
    feedback:
      'The carbohydrate has been left out. Sugars and starch supply energy too: add carbohydrate × 16 kJ g⁻¹.',
  },
];

function farEnough(value: number, chosen: number[]): boolean {
  return chosen.every(
    (other) => Math.abs(value - other) >= MIN_OPTION_GAP * Math.max(Math.abs(other), 1),
  );
}

/** Four-option energy estimate with misconception distractors; null for an unknown food id. */
export function foodEnergyQuestion(foodId: string): ChoiceQuestion | null {
  const index = FOOD_SAMPLES.findIndex((item) => item.id === foodId);
  if (index < 0) return null;
  const food = FOOD_SAMPLES[index];
  const energy = foodEnergy(food.grams)!;
  const correct = roundKilojoules(energy.totalKilojoules);
  const working = energy.contributions
    .map((item) => `${item.grams} × ${item.factor}`)
    .join(' + ');
  const values = [correct];
  const options: ChoiceOption[] = [
    {
      id: 'correct',
      label: `${correct} kJ`,
      correct: true,
      feedback: `Correct: ${working} = ${correct} kJ for ${food.serve}.`,
    },
  ];
  for (const distractor of ENERGY_DISTRACTORS) {
    if (options.length === ENERGY_OPTION_COUNT) break;
    const value = roundKilojoules(distractor.value(food.grams));
    if (!farEnough(value, values)) continue;
    values.push(value);
    options.push({
      id: distractor.id,
      label: `${value} kJ`,
      correct: false,
      feedback: `${distractor.feedback} Working: ${working} = ${correct} kJ.`,
    });
  }
  return {
    id: `energy-${food.id}`,
    prompt: `${food.name} (${food.serve}) has ${food.grams.protein} g protein, ${food.grams.carbohydrate} g carbohydrate and ${food.grams.fat} g fat. About how much energy does it supply?`,
    options: [...options.slice(index % options.length), ...options.slice(0, index % options.length)],
  };
}
